import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Input, Select, DatePicker, Checkbox, Button, Table, Space,
  message, Typography, Divider, Tag, Spin } from 'antd';
import { SaveOutlined, FilePdfOutlined, PlusOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import useAvisoAlteracoes from '../hooks/useAvisoAlteracoes';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

const DEFEITOS = [
  { value: 'TL', label: 'TL – Trinca longitudinal' },
  { value: 'TT', label: 'TT – Trinca transversal' },
  { value: 'PO', label: 'PO – Porosidade' },
  { value: 'MO', label: 'MO – Mordedura' },
  { value: 'SO', label: 'SO – Sobreposição' },
  { value: 'FF', label: 'FF – Falta de fusão' },
  { value: 'FP', label: 'FP – Falta de penetração' },
  { value: 'OUTRO', label: 'Outro (descrever)' },
];

const RESULTADOS = [
  { value: 'A', label: 'A – Aprovado' },
  { value: 'R', label: 'R – Reprovado' },
  { value: 'REC', label: 'REC – Recomendação de exame complementar' },
];

const CLASSIF_REVELADOR = ['Seco', 'Solúvel em água', 'Suspensível em água', 'Não aquoso'];

let SEQ = 1;
const chave = () => 'k' + (SEQ++);

function FotoSecao({ sid, idx, legenda, aoRemover }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let vivo = true; let url = null;
    api.get('/ensaios-lp/secoes/' + sid + '/fotos/' + idx, { responseType: 'blob' })
      .then((r) => { if (!vivo) return; url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => {});
    return () => { vivo = false; if (url) URL.revokeObjectURL(url); };
  }, [sid, idx]);
  return (
    <div style={{ width: 150, marginRight: 10, marginBottom: 10 }}>
      <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, height: 110,
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: '#fafafa' }}>
        {src ? <img src={src} alt={legenda || 'foto'} style={{ maxWidth: '100%', maxHeight: '100%' }} />
             : <Spin size="small" />}
      </div>
      <Button size="small" danger block style={{ marginTop: 4 }} icon={<DeleteOutlined />}
        onClick={aoRemover}>remover</Button>
    </div>
  );
}

function Consumivel({ titulo, pre, d, set, extra }) {
  const g = (c) => d[pre + '_' + c];
  const s = (c) => (v) => set(pre + '_' + c, v);
  return (
    <>
      <Divider orientation="left" style={{ margin: '12px 0 8px' }}>{titulo}</Divider>
      <Row gutter={8}>
        <Col span={5}><Text type="secondary">Marca</Text>
          <Input value={g('marca') || ''} onChange={(e) => s('marca')(e.target.value)} /></Col>
        <Col span={4}><Text type="secondary">Tipo</Text>
          <Input value={g('tipo') || ''} onChange={(e) => s('tipo')(e.target.value)} /></Col>
        <Col span={5}><Text type="secondary">Lote</Text>
          <Input value={g('lote') || ''} onChange={(e) => s('lote')(e.target.value)} /></Col>
        <Col span={4}><Text type="secondary">Validade do lote</Text>
          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
            value={g('validade') ? dayjs(String(g('validade')).substring(0, 10)) : null}
            onChange={(v) => s('validade')(v ? v.format('YYYY-MM-DD') : null)} /></Col>
        <Col span={3}><Text type="secondary">Tempo</Text>
          <Input value={g('tempo') || ''} onChange={(e) => s('tempo')(e.target.value)} placeholder="20 min" /></Col>
        <Col span={3} style={{ paddingTop: 22 }}>
          <Checkbox checked={!!g('certificado')} onChange={(e) => s('certificado')(e.target.checked)}>Certificado</Checkbox>
        </Col>
      </Row>
      <Row gutter={8} style={{ marginTop: 6 }}>
        <Col span={5} style={{ paddingTop: 4 }}>
          <Checkbox checked={!!g('dois_lotes')} onChange={(e) => s('dois_lotes')(e.target.checked)}>
            Usei mais de um lote
          </Checkbox>
        </Col>
        {g('dois_lotes') && (
          <>
            <Col span={5}><Text type="secondary">Lote 2</Text>
              <Input value={g('lote2') || ''} onChange={(e) => s('lote2')(e.target.value)} /></Col>
            <Col span={4}><Text type="secondary">Validade do lote 2</Text>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                value={g('validade2') ? dayjs(String(g('validade2')).substring(0, 10)) : null}
                onChange={(v) => s('validade2')(v ? v.format('YYYY-MM-DD') : null)} /></Col>
          </>
        )}
        {extra}
      </Row>
    </>
  );
}

function EnsaioLP() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const naoSalvo = useAvisoAlteracoes(d);
  const [equips, setEquips] = useState([]);
  const [secoes, setSecoes] = useState([]);
  const [luximetros, setLuximetros] = useState([]);
  const [salvando, setSalvando] = useState(false);

  const set = (c, v) => setD((x) => ({ ...x, [c]: v }));
  const setSec = (i, c, v) => setSecoes((l) => l.map((s, k) => (k === i ? { ...s, [c]: v } : s)));
  const setEq = (i, c, v) => setEquips((l) => l.map((e, k) => (k === i ? { ...e, [c]: v } : e)));

  const carregar = useCallback(async () => {
    try {
      const r = await api.get('/ensaios-lp/' + id);
      setD(r.data);
      const eq = (r.data.equipamentos || []).map((e) => ({ ...e, _k: chave() }));
      setEquips(eq);
      setSecoes((r.data.secoes || []).map((s) => ({
        ...s, _k: chave(),
        _eq: (eq.find((e) => e.id === s.equipamento_id) || {})._k || null,
      })));
    } catch { message.error('Não foi possível abrir o ensaio'); }
  }, [id]);

  useEffect(() => {
    carregar();
    const pega = (rota) => api.get(rota).then((r) => {
      const lista = (r.data || []).filter((p) => p.tipo === 'luximetro');
      if (lista.length) setLuximetros(lista);
    }).catch(() => {});
    pega('/padroes-calibracao');
    pega('/padroes');
  }, [carregar]);

  // ordena: cada equipamento com as suas, e por ultimo as avulsas
  const ordenadas = () => {
    const fora = [];
    equips.forEach((e) => secoes.forEach((s) => { if (s._eq === e._k) fora.push({ s, idx: equips.indexOf(e) }); }));
    secoes.forEach((s) => { if (!s._eq || !equips.some((e) => e._k === s._eq)) fora.push({ s, idx: null }); });
    return fora;
  };

  const salvar = async (depois) => {
    setSalvando(true);
    try {
      const lista = ordenadas();
      const corpo = {
        ...d,
        equipamentos: equips.map((e) => ({ nome: e.nome || null, tag: e.tag || null })),
        secoes: lista.map((x, i) => ({ ...x.s, item: i + 1, equip_idx: x.idx })),
      };
      const r = await api.put('/ensaios-lp/' + id, corpo);
      setD(r.data);
      const eq = (r.data.equipamentos || []).map((e) => ({ ...e, _k: chave() }));
      setEquips(eq);
      setSecoes((r.data.secoes || []).map((s) => ({
        ...s, _k: chave(),
        _eq: (eq.find((e) => e.id === s.equipamento_id) || {})._k || null,
      })));
      naoSalvo.limpo();
      message.success('Ensaio salvo');
      if (depois) depois();
    } catch (e) { message.error('Erro ao salvar: ' + (e.response?.data?.erro || e.message)); }
    finally { setSalvando(false); }
  };

  const gerarPDF = () => salvar(async () => {
    message.loading({ content: 'Gerando PDF...', key: 'pdf', duration: 0 });
    try {
      const resp = await api.get('/ensaios-lp/' + id + '/pdf', { responseType: 'blob' });
      window.open(window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })), '_blank');
      message.destroy('pdf');
      carregar();
    } catch { message.destroy('pdf'); message.error('Erro ao gerar PDF'); }
  });

  if (!d) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;

  const addSecao = (eqK) => setSecoes((l) => [...l, {
    _k: chave(), _eq: eqK || null, resultado: 'A',
    temperatura: d.temperatura || '', tratamento_termico: d.tratamento_termico || '',
    processo_soldagem: d.processo_soldagem || '', condicoes_superficiais: d.condicoes_superficiais || '',
  }]);

  const posicao = (r) => {
    const k = ordenadas().findIndex((x) => x.s._k === r._k);
    return k >= 0 ? k + 1 : '-';
  };

  const bloco = (r) => {
    const rep = r.resultado === 'R';
    return (
      <Card key={r._k} size="small" style={{ marginBottom: 10, borderColor: rep ? '#ffccc7' : '#e8e8e8' }}
        title={<Space size={6}>
          <Text type="secondary" style={{ fontSize: 12 }}>ITEM</Text>
          <Text strong>{posicao(r)}</Text>
          {r.tag ? <Tag>{r.tag}</Tag> : null}
          {rep ? <Tag color="red">reprovada</Tag> : null}
        </Space>}
        extra={<Button size="small" danger icon={<DeleteOutlined />} title="Remover seção"
          onClick={() => setSecoes((l) => l.filter((x) => x._k !== r._k))} />}>

        <Row gutter={8}>
          <Col span={4}><Text type="secondary">Nº da seção</Text>
            <Input size="small" value={r.numero_secao || ''}
              onChange={(e) => setSec(r._i, 'numero_secao', e.target.value)} /></Col>
          <Col span={5}><Text type="secondary">TAG</Text>
            <Input size="small" value={r.tag || ''}
              onChange={(e) => setSec(r._i, 'tag', (e.target.value || '').toUpperCase())} /></Col>
          <Col span={5}><Text type="secondary">Material</Text>
            <Input size="small" value={r.material || ''}
              onChange={(e) => setSec(r._i, 'material', e.target.value)} /></Col>
          <Col span={10}><Text type="secondary">Área da solda / seção</Text>
            <Input size="small" value={r.area_solda || ''}
              onChange={(e) => setSec(r._i, 'area_solda', e.target.value)} /></Col>
        </Row>

        <Row gutter={8} style={{ marginTop: 8 }}>
          <Col span={9}><Text type="secondary">Descrição</Text>
            <Input size="small" value={r.descricao || ''}
              onChange={(e) => setSec(r._i, 'descricao', e.target.value)} /></Col>
          <Col span={4}><Text type="secondary">Temperatura</Text>
            <Input size="small" value={r.temperatura || ''} placeholder={d.temperatura || '25 °C'}
              onChange={(e) => setSec(r._i, 'temperatura', e.target.value)} /></Col>
          <Col span={5}><Text type="secondary">Tratamento térmico</Text>
            <Input size="small" value={r.tratamento_termico || ''} placeholder={d.tratamento_termico || ''}
              onChange={(e) => setSec(r._i, 'tratamento_termico', e.target.value)} /></Col>
          <Col span={6}><Text type="secondary">Processo de soldagem</Text>
            <Input size="small" value={r.processo_soldagem || ''} placeholder={d.processo_soldagem || ''}
              onChange={(e) => setSec(r._i, 'processo_soldagem', e.target.value)} /></Col>
        </Row>

        <Row gutter={8} style={{ marginTop: 8 }}>
          <Col span={7}><Text type="secondary">Condições superficiais</Text>
            <Input size="small" value={r.condicoes_superficiais || ''} placeholder={d.condicoes_superficiais || ''}
              onChange={(e) => setSec(r._i, 'condicoes_superficiais', e.target.value)} /></Col>
          <Col span={10}><Text type="secondary">Tipo de defeito</Text>
            <Select size="small" mode="multiple" allowClear style={{ width: '100%' }} options={DEFEITOS}
              value={r.tipo_defeito ? String(r.tipo_defeito).split(',').filter(Boolean) : []}
              onChange={(v) => setSec(r._i, 'tipo_defeito', v.join(','))} /></Col>
          <Col span={7}><Text type="secondary">Laudo</Text>
            <Select size="small" style={{ width: '100%' }} options={RESULTADOS} value={r.resultado || undefined}
              placeholder="—" onChange={(v) => setSec(r._i, 'resultado', v)} /></Col>
        </Row>

        {String(r.tipo_defeito || '').includes('OUTRO') && (
          <Row gutter={8} style={{ marginTop: 8 }}>
            <Col span={10}><Text type="secondary">Qual defeito</Text>
              <Input size="small" value={r.tipo_defeito_outro || ''}
                onChange={(e) => setSec(r._i, 'tipo_defeito_outro', e.target.value)} /></Col>
          </Row>
        )}

        {rep && (
          <Card size="small" style={{ marginTop: 10, background: '#fffbe6' }} title="Seção reprovada">
            <Row gutter={8}>
              <Col span={12}><Text type="secondary">Dimensão da indicação</Text>
                <TextArea rows={3} value={r.dimensao || ''}
                  onChange={(e) => setSec(r._i, 'dimensao', e.target.value)}
                  placeholder="Tamanho e extensão da indicação encontrada" /></Col>
              <Col span={12}><Text type="secondary">Recomendação</Text>
                <TextArea rows={3} value={r.justificativa || ''}
                  onChange={(e) => setSec(r._i, 'justificativa', e.target.value)}
                  placeholder="O que recomendar diante da indicação" /></Col>
            </Row>
            <Divider style={{ margin: '12px 0 8px' }} orientation="left">Fotos da peça</Divider>
            {!r.id ? (
              <Text type="secondary">Salve o laudo uma vez para poder anexar fotos nesta seção.</Text>
            ) : (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {(r.fotos || []).map((f, k) => (
                    <FotoSecao key={k} sid={r.id} idx={k} legenda={f.legenda}
                      aoRemover={async () => {
                        try {
                          const resp = await api.delete('/ensaios-lp/secoes/' + r.id + '/fotos/' + k);
                          setSec(r._i, 'fotos', resp.data.fotos || []);
                          message.success('Foto removida');
                        } catch { message.error('Erro ao remover foto'); }
                      }} />
                  ))}
                </div>
                <input type="file" accept="image/*" multiple onChange={async (ev) => {
                  const arquivos = Array.from(ev.target.files || []);
                  if (!arquivos.length) return;
                  message.loading({ content: 'Enviando foto(s)...', key: 'fotolp', duration: 0 });
                  try {
                    let ultimas = r.fotos || [];
                    for (const f of arquivos) {
                      const fd = new FormData();
                      fd.append('foto', f);
                      const resp = await api.post('/ensaios-lp/secoes/' + r.id + '/fotos', fd,
                        { headers: { 'Content-Type': 'multipart/form-data' } });
                      ultimas = resp.data.fotos || ultimas;
                    }
                    setSec(r._i, 'fotos', ultimas);
                    message.success({ content: 'Foto(s) enviada(s)', key: 'fotolp' });
                  } catch (e) {
                    message.error({ content: 'Erro ao enviar: ' + (e.response?.data?.erro || e.message), key: 'fotolp' });
                  }
                  ev.target.value = '';
                }} />
                <Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
                  As fotos entram na página desta seção no PDF, duas por linha.
                </Text>
              </>
            )}
          </Card>
        )}
      </Card>
    );
  };

  const tabela = (lista) => (lista.length
    ? lista.map(bloco)
    : <Text type="secondary">Nenhuma seção ainda.</Text>);

  const comIndice = (filtro) => secoes.map((s, i) => ({ ...s, _i: i })).filter(filtro);
  const avulsas = comIndice((s) => !s._eq || !equips.some((e) => e._k === s._eq));
  const reprovadas = secoes.filter((s) => s.resultado === 'R').length;

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/laudos')}>Voltar</Button>
        <Title level={4} style={{ margin: 0 }}>Ensaio por Líquido Penetrante</Title>
        {d.codigo_end ? <Tag color="blue">{d.codigo_end}</Tag> : <Tag>código a gerar</Tag>}
        {d.numero_os ? <Tag>OS {d.numero_os}</Tag> : null}
      </Space>

      <Card size="small" title="Identificação" style={{ marginBottom: 12 }}>
        <Row gutter={8}>
          <Col span={10}><Text type="secondary">Cliente</Text>
            <Input value={d.razao_social || d.nome_cliente || ''} disabled /></Col>
          <Col span={7}><Text type="secondary">Inspetor (quem criou o laudo)</Text>
            <Input disabled value={(d.inspetor_nome || '-') + (d.inspetor_qualificacao ? ' · ' + d.inspetor_qualificacao : '')} /></Col>
          <Col span={7}><Text type="secondary">Assina pela empresa</Text>
            <Input disabled value={(d.responsavel_nome || '-') + (d.responsavel_crea ? ' · ' + d.responsavel_crea : '')} /></Col>
        </Row>
        <Row gutter={8} style={{ marginTop: 8 }}>
          <Col span={5}><Text type="secondary">Data de início</Text>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
              value={d.data_inicio ? dayjs(String(d.data_inicio).substring(0, 10)) : null}
              onChange={(v) => set('data_inicio', v ? v.format('YYYY-MM-DD') : null)} /></Col>
          <Col span={5}><Text type="secondary">Data de término</Text>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
              value={d.data_termino ? dayjs(String(d.data_termino).substring(0, 10)) : null}
              onChange={(v) => set('data_termino', v ? v.format('YYYY-MM-DD') : null)} /></Col>
          <Col span={14} style={{ paddingTop: 22 }}>
            <Checkbox checked={!!d.usar_endereco_cnpj} onChange={(e) => set('usar_endereco_cnpj', e.target.checked)}>
              Usar endereço do CNPJ (ensaio feito na Celum)
            </Checkbox>
          </Col>
        </Row>
      </Card>

      <Card size="small" title="Peça e condições" style={{ marginBottom: 12 }}
        extra={<Text type="secondary">Preenche aqui e desce para toda seção nova. Dá para mudar seção por seção.</Text>}>
        <Row gutter={8}>
          <Col span={6}><Text type="secondary">Tratamento térmico</Text>
            <Input value={d.tratamento_termico || ''} onChange={(e) => set('tratamento_termico', e.target.value)} /></Col>
          <Col span={6}><Text type="secondary">Processo de soldagem</Text>
            <Input value={d.processo_soldagem || ''} onChange={(e) => set('processo_soldagem', e.target.value)} /></Col>
          <Col span={7}><Text type="secondary">Condições superficiais</Text>
            <Input value={d.condicoes_superficiais || ''} onChange={(e) => set('condicoes_superficiais', e.target.value)} /></Col>
          <Col span={5}><Text type="secondary">Temperatura da peça</Text>
            <Input value={d.temperatura || ''} onChange={(e) => set('temperatura', e.target.value)} placeholder="25 °C" /></Col>
        </Row>
      </Card>

      <Card size="small" title="Consumíveis e iluminação" style={{ marginBottom: 12 }}>
        <Consumivel titulo="Penetrante" pre="pen" d={d} set={set}
          extra={<Col span={5}><Text type="secondary">Método</Text>
            <Input value={d.pen_metodo || ''} onChange={(e) => set('pen_metodo', e.target.value)} placeholder="tipo II A" /></Col>} />
        <Consumivel titulo="Removedor" pre="rem" d={d} set={set} />
        <Consumivel titulo="Revelador" pre="rev" d={d} set={set}
          extra={<>
            <Col span={5}><Text type="secondary">Classificação</Text>
              <Select style={{ width: '100%' }} allowClear value={d.rev_classificacao || undefined}
                onChange={(v) => set('rev_classificacao', v)}
                options={CLASSIF_REVELADOR.map((x) => ({ value: x, label: x }))} /></Col>
            <Col span={4}><Text type="secondary">Limpeza final</Text>
              <Input value={d.rev_limpeza_final || ''} onChange={(e) => set('rev_limpeza_final', e.target.value)} placeholder="FEITO" /></Col>
          </>} />
        <Divider orientation="left" style={{ margin: '12px 0 8px' }}>Iluminação</Divider>
        <Row gutter={8}>
          <Col span={5}><Text type="secondary">Intensidade de luz branca</Text>
            <Input value={d.luz_branca || ''} onChange={(e) => set('luz_branca', e.target.value)} placeholder="1450 LUX" /></Col>
          <Col span={5}><Text type="secondary">Intensidade de luz negra</Text>
            <Input value={d.luz_negra || ''} onChange={(e) => set('luz_negra', e.target.value)} placeholder="—" /></Col>
          <Col span={8}><Text type="secondary">Luxímetro utilizado</Text>
            <Select style={{ width: '100%' }} allowClear value={d.luximetro_id || undefined}
              onChange={(v) => set('luximetro_id', v)}
              placeholder={luximetros.length ? 'Escolha o luxímetro' : 'Nenhum luxímetro cadastrado ainda'}
              options={luximetros.map((p) => ({ value: p.id, label: p.identificacao + (p.certificado ? ' · ' + p.certificado : '') }))} /></Col>
        </Row>
      </Card>

      <Card size="small" style={{ marginBottom: 12 }}
        title={<Space>Seções inspecionadas <Tag>{secoes.length}</Tag>
          {reprovadas > 0 && <Tag color="red">{reprovadas} reprovada(s)</Tag>}</Space>}
        extra={<Button size="small" icon={<PlusOutlined />}
          onClick={() => setEquips((l) => [...l, { _k: chave(), nome: '', tag: '' }])}>
          Adicionar equipamento</Button>}>

        {equips.map((e, ei) => (
          <Card key={e._k} size="small" style={{ marginBottom: 10, borderColor: '#91caff' }}
            title={
              <Space>
                <Input size="small" style={{ width: 240 }} placeholder="Nome do equipamento"
                  value={e.nome || ''} onChange={(ev) => setEq(ei, 'nome', ev.target.value)} />
                <Input size="small" style={{ width: 140 }} placeholder="TAG"
                  value={e.tag || ''} onChange={(ev) => setEq(ei, 'tag', (ev.target.value || '').toUpperCase())} />
              </Space>
            }
            extra={
              <Space>
                <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => addSecao(e._k)}>
                  Adicionar seção</Button>
                <Button size="small" danger icon={<DeleteOutlined />} title="Remover equipamento (as seções viram avulsas)"
                  onClick={() => {
                    setSecoes((l) => l.map((s) => (s._eq === e._k ? { ...s, _eq: null } : s)));
                    setEquips((l) => l.filter((x) => x._k !== e._k));
                  }} />
              </Space>
            }>
            {tabela(comIndice((s) => s._eq === e._k))}
          </Card>
        ))}

        <Card size="small" title="Seções sem equipamento" style={{ borderColor: '#d9d9d9' }}
          extra={<Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => addSecao(null)}>
            Adicionar seção</Button>}>
          {tabela(avulsas)}
        </Card>
      </Card>

      <Card size="small" title="Resultado geral" style={{ marginBottom: 12 }}>
        <Input value={d.resultado_geral || ''} onChange={(e) => set('resultado_geral', e.target.value)}
          placeholder="Deixe vazio para o sistema contar as seções sozinho" />
      </Card>

      <Space style={{ marginBottom: 24 }}>
        <Button type="primary" icon={<SaveOutlined />} loading={salvando} onClick={() => salvar()}>Salvar</Button>
        <Button icon={<FilePdfOutlined />} style={{ color: '#cf1322', borderColor: '#cf1322' }}
          onClick={gerarPDF}>Salvar e gerar PDF</Button>
      </Space>
    </div>
  );
}

export default EnsaioLP;
